<script setup lang="ts">
const props = defineProps({
  postId: { type: String, required: true },
  postTitle: { type: String, required: true },
  userId: { type: String, required: true },
  isPinned: { type: Boolean, required: true },
  isAnnouncement: { type: Boolean, required: true },
  isResolved: { type: Boolean, required: true },
  isAdmin: { type: Boolean, required: true },
  isBookmark: { type: Boolean, required: true },
  isFollow: { type: Boolean, required: true },
  isAuthor: { type: Boolean, required: true },
  isAuthenticated: { type: Boolean, default: false },
  isClassroomPost: { type: Boolean, default: false },
  isSupport: { type: Boolean, default: false }
})

// router
const emit = defineEmits(['refreshList', 'refreshPost'])

// REFS
const showMenu = ref(false)

// --------------------
// FORUM-BACKEND
// --------------------

const { followPost, unfollowPost } = useForumFollowedPost()

const clickFollow = () => {
  showMenu.value = false

  try {
    if (props.isAuthenticated) {
      followPost(props.postId)
      emit('refreshPost')
    }
  } catch (error) {
    console.error('Error toggling follow post:', error)
  }
}

const clickUnfollow = () => {
  showMenu.value = false

  try {
    if (props.isAuthenticated) {
      unfollowPost(props.postId)
      emit('refreshPost')
    }
  } catch (error) {
    console.error('Error toggling unfollow post:', error)
  }
}

const clickReport = () => {
  showMenu.value = false
  if (props.isAuthenticated) navigateTo(`/forum/report/add?post=${props.postId}`)
}

const clickPin = () => {
  showMenu.value = false
  if (props.isAuthenticated) {
    updatePost(props.postId, { group_name: ['PINNED_QUESTION'] })
    emit('refreshPost')
  }
}

const { bookmarkPost, unbookmarkPost } = useForumBookmark()
const { deletePost, updatePost } = useForumPost()

const clickBookmark = async () => {
  showMenu.value = false
  try {
    await bookmarkPost({ post_id_slug: props.postId })
    emit('refreshPost')
  } catch (error) {
    console.error('Error toggling bookmark:', error)
  }
}

const clickRemoveBookmark = async () => {
  showMenu.value = false
  try {
    await unbookmarkPost({ post_id_slug: props.postId })
    emit('refreshPost')
  } catch (error) {
    console.error('Error toggling bookmark:', error)
  }
}

const clickResolve = async () => {
  showMenu.value = false

  if (props.isAuthenticated && props.isAdmin) {
    await updatePost(props.postId, { is_resolved: true })
    emit('refreshPost')
  }
}

const clickUnresolve = async () => {
  showMenu.value = false
  if (props.isAuthenticated && props.isAdmin) {
    // Confirm first with user, warn them that answer will be removed
    const message =
      'Are you sure you want to set this post as unresolved? \nWith this action, the marked as answer will be removed.'

    if (confirm(message)) {
      await updatePost(props.postId, { is_resolved: false })
      emit('refreshPost')
    }
  }
}

const clickDelete = async () => {
  showMenu.value = false
  if (props.isAuthenticated) {
    // confirm first before deleting
    if (confirm('Are you sure you want to delete this post?')) {
      await deletePost(props.postId)
      emit('refreshList')
    }
  }
}

// --------------------
// VIEW MODEL
// --------------------

// share
const { sharePageLink } = useShare()

const clickMenu = () => {
  showMenu.value = !showMenu.value
}

const clickShare = () => {
  showMenu.value = false
  const url = `https://www.talentlabs.org/forum/post/${props.postId}`
  const title = props.postTitle
  const text = 'Check out this post on TalentLabs!'
  const msg = `Check out this post on TalentLabs: \n${props.postTitle}`
  sharePageLink(url, title, text, msg)
}

const removeMenu = () => {
  showMenu.value = false
}
</script>

<template>
  <div class="post-menu-btn" @click.stop="clickMenu">
    <CommonButton square grey>
      <CommonIconForumMenu />
    </CommonButton>

    <CommonMenuDropdown :show-menu="showMenu" @close-menu="removeMenu">
      <!-- Only authors can edit post -->
      <div
        v-if="isAuthenticated && isAuthor"
        @click.stop="navigateTo(`/forum/post/edit/${postId}`)"
      >
        <CommonIconProfileIconEdit />
        <span>Edit post</span>
      </div>

      <!-- Everyone can share post -->
      <div @click.stop="clickShare">
        <CommonIconForumShare />
        <span>Share post</span>
      </div>

      <!-- Author no need to follow their own post -->
      <!-- Admin see follow button directly -->
      <!-- Unfollow post if follow -->
      <div
        v-if="
          isAuthenticated &&
          !isAuthor &&
          !isAdmin &&
          isFollow &&
          !isSupport &&
          !isResolved &&
          !isAnnouncement
        "
        @click.stop="clickUnfollow"
      >
        <CommonIconForumCheck />
        <span>Unfollow post</span>
      </div>

      <!-- Follow post if unfollow -->
      <div
        v-if="
          isAuthenticated &&
          !isAuthor &&
          !isAdmin &&
          !isFollow &&
          !isSupport &&
          !isResolved &&
          !isAnnouncement
        "
        @click.stop="clickFollow"
      >
        <CommonIconForumAdd />
        <span>Follow post</span>
      </div>

      <!-- Author no need to remove bookmark for their own post -->
      <!-- Remove bookmark if bookmarked -->
      <div
        v-if="isAuthenticated && !isAuthor && isBookmark && !isSupport"
        @click.stop="clickRemoveBookmark"
      >
        <CommonIconForumBookmark />
        <span>Remove bookmark</span>
      </div>

      <!-- Author no need to bookmark their own post -->
      <!-- Bookmark if not yet -->
      <div
        v-if="isAuthenticated && !isAuthor && !isBookmark && !isSupport"
        @click.stop="clickBookmark"
      >
        <CommonIconForumBookmark />
        <span>Bookmark post</span>
      </div>

      <!-- Author or admin can mark post as resolved -->
      <div v-if="isAuthenticated && isAdmin && !isResolved" @click.stop="clickResolve">
        <CommonIconCheck />
        <span>Set as resolved</span>
      </div>

      <!-- Author or admin can mark post as unresolved -->
      <div v-if="isAuthenticated && isAdmin && isResolved" @click.stop="clickUnresolve">
        <CommonIconClose />
        <span>Remove resolve</span>
      </div>

      <!-- Admin can pin post -->
      <div
        v-if="isAuthenticated && isAdmin && !isPinned && isClassroomPost && !isSupport"
        @click.stop="clickPin"
      >
        <CommonIconForumPin />
        <span>Pin post</span>
      </div>

      <!-- Admin can unpin post -->
      <div
        v-else-if="isAuthenticated && isAdmin && isPinned && isClassroomPost && !isSupport"
        @click.stop="clickPin"
      >
        <CommonIconForumPin />
        <span>Remove pin</span>
      </div>

      <!-- Author or admin can delete post -->
      <div v-if="isAuthenticated && (isAdmin || isAuthor) && !isSupport" @click.stop="clickDelete">
        <CommonIconProfileIconDeleteOutline />
        <span>Delete post</span>
      </div>

      <!-- Anyone who's not the author and not admin can report post -->
      <div v-if="isAuthenticated && !isAuthor && !isAdmin && !isSupport" @click.stop="clickReport">
        <CommonIconForumFlag />
        <span>Report</span>
      </div>
    </CommonMenuDropdown>
  </div>
</template>
